import { useEffect, useState } from 'react';
import { apiClient } from '../utils/apiClient';
import type { NeedsActionSummary } from '../components/transactions/kiosk/types';

export const NEEDS_ACTION_POLL_MS = 60000;
export const KIOSK_NEEDS_ACTION_CHANGED_EVENT = 'kiosk-needs-action:changed';

type Listener = (summary: NeedsActionSummary | null) => void;

const listeners = new Set<Listener>();
let latest: NeedsActionSummary | null = null;
let timer: number | null = null;

export function announceKioskNeedsActionChanged(): void {
  window.dispatchEvent(new Event(KIOSK_NEEDS_ACTION_CHANGED_EVENT));
}

function load(): void {
  apiClient
    .get<{ data: NeedsActionSummary }>('/api/backend/kiosk-transactions/needs-action/summary')
    .then((response) => {
      latest = response.data;
      listeners.forEach((listener) => listener(latest));
    })
    .catch((error) => console.error('[Kiosk Needs action] summary failed', error));
}

function start(): void {
  load();
  timer = window.setInterval(load, NEEDS_ACTION_POLL_MS);
  window.addEventListener(KIOSK_NEEDS_ACTION_CHANGED_EVENT, load);
}

function stop(): void {
  if (timer !== null) window.clearInterval(timer);
  timer = null;
  latest = null;
  window.removeEventListener(KIOSK_NEEDS_ACTION_CHANGED_EVENT, load);
}

export function useKioskNeedsActionSummary(enabled: boolean): NeedsActionSummary | null {
  const [summary, setSummary] = useState<NeedsActionSummary | null>(null);
  useEffect(() => {
    if (!enabled) {
      setSummary(null);
      return undefined;
    }
    listeners.add(setSummary);
    if (listeners.size === 1) start();
    else setSummary(latest);
    return () => {
      listeners.delete(setSummary);
      if (listeners.size === 0) stop();
    };
  }, [enabled]);
  return summary;
}
